import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Clock,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  CloudRain,
  Calendar,
  Download,
  Printer,
  ArrowLeft,
} from 'lucide-react';
import { exportTimeline } from '../features/timelinePortable';
import { ImportTimelineButton } from '../components/event-ops/ImportTimelineButton';
import type { Timeline, TimelineSection as TSection, TimelineEntry } from '@peles/shared';
import {
  useTimelines,
  useCreateTimeline,
  useDeleteTimeline,
  useUpdateTimeline,
  useAddTimelineSection,
  useUpdateTimelineSection,
  useDeleteTimelineSection,
  useAddTimelineEntry,
  useUpdateTimelineEntry,
  useDeleteTimelineEntry,
} from '../hooks/useEventOps';
import { useEvent, useEventTags } from '../hooks/useEvents';
import { SaveAsTemplateButton } from '../components/event-ops/SaveAsTemplateButton';
import { useQuery } from '@tanstack/react-query';
import { getAppLogoUrl } from '../api/events';
import { TimelinePrintView } from '../components/timeline/TimelinePrintView';
import { useDialog } from '../context/DialogContext';

function formatEventDateRange(startAt: Date | string, endAt: Date | string | null): string {
  const start = new Date(startAt);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  if (!endAt) return start.toLocaleDateString('en-US', opts);
  const end = new Date(endAt);
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.getDate()}, ${start.getFullYear()}`;
  }
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${sameYear ? `, ${start.getFullYear()}` : ''}`;
}

export function formatTimeDisplay(time: string | null | undefined): string {
  if (!time) return '';
  const match = time.match(/^(\d{2}):(\d{2})$/);
  if (!match) return time;
  let h = parseInt(match[1], 10);
  const m = match[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m}${ampm}`;
}

type EntryFormState = {
  time: string;
  title: string;
  location: string;
  vendorNote: string;
  notes: string;
};
const emptyForm: EntryFormState = { time: '', title: '', location: '', vendorNote: '', notes: '' };

function entryToForm(entry: TimelineEntry): EntryFormState {
  return {
    time: entry.time ?? '',
    title: entry.title,
    location: entry.location ?? '',
    vendorNote: entry.vendorNote ?? '',
    notes: entry.notes ?? '',
  };
}

export function TimelinePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { data: timelines = [], isLoading } = useTimelines(eventId);
  const { data: event } = useEvent(eventId ?? '');
  const createTimeline = useCreateTimeline();
  const deleteTimeline = useDeleteTimeline();
  const updateTimeline = useUpdateTimeline();
  const addSection = useAddTimelineSection();
  const updateSection = useUpdateTimelineSection();
  const deleteSection = useDeleteTimelineSection();
  const addEntry = useAddTimelineEntry();
  const updateEntry = useUpdateTimelineEntry();
  const deleteEntry = useDeleteTimelineEntry();

  const [expandedTimeline, setExpandedTimeline] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [newSectionName, setNewSectionName] = useState('');

  // Section renaming
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [sectionNameInput, setSectionNameInput] = useState('');

  // Entry add form: tracks sectionId
  const [addingEntryToSection, setAddingEntryToSection] = useState<string | null>(null);
  const [addEntryForm, setAddEntryForm] = useState<EntryFormState>(emptyForm);

  // Entry edit form: tracks entryId
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editEntryForm, setEditEntryForm] = useState<EntryFormState>(emptyForm);

  const { data: eventTags = [] } = useEventTags(eventId);
  const { data: appLogoUrl } = useQuery({
    queryKey: ['app-logo-url'],
    queryFn: getAppLogoUrl,
    staleTime: 60_000,
  });
  const [printTimeline, setPrintTimeline] = useState<Timeline | null>(null);

  const isEventScoped = !!eventId;

  const handleCreateTimeline = useCallback(() => {
    createTimeline.mutate({ eventId, title: 'New Timeline' });
  }, [createTimeline, eventId]);

  const handleDeleteTimeline = useCallback(
    async (id: string) => {
      if (
        await confirm({
          title: 'Delete Timeline',
          message: 'Delete this timeline?',
          confirmLabel: 'Delete',
        })
      ) {
        deleteTimeline.mutate(id);
      }
    },
    [deleteTimeline, confirm],
  );

  const toggleTimeline = useCallback((id: string) => {
    setExpandedTimeline((prev) => (prev === id ? null : id));
  }, []);

  const toggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddSection = useCallback(
    (timelineId: string) => {
      if (!newSectionName.trim()) return;
      addSection.mutate({ timelineId, name: newSectionName.trim() });
      setNewSectionName('');
    },
    [addSection, newSectionName],
  );

  const startEditSection = useCallback((section: TSection) => {
    setEditingSectionId(section.id);
    setSectionNameInput(section.name);
  }, []);

  const saveEditSection = useCallback(() => {
    if (!editingSectionId || !sectionNameInput.trim()) return;
    updateSection.mutate(
      { sectionId: editingSectionId, updates: { name: sectionNameInput.trim() } },
      { onSuccess: () => setEditingSectionId(null) },
    );
  }, [editingSectionId, sectionNameInput, updateSection]);

  const cancelEditSection = useCallback(() => {
    setEditingSectionId(null);
    setSectionNameInput('');
  }, []);

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      deleteSection.mutate(sectionId);
    },
    [deleteSection],
  );

  const handleAddEntry = useCallback(
    (sectionId: string) => {
      if (!addEntryForm.title.trim()) return;
      addEntry.mutate(
        {
          sectionId,
          time: addEntryForm.time || undefined,
          title: addEntryForm.title.trim(),
          location: addEntryForm.location || undefined,
          vendorNote: addEntryForm.vendorNote || undefined,
          notes: addEntryForm.notes || undefined,
        },
        {
          onSuccess: () => {
            setAddEntryForm(emptyForm);
            setAddingEntryToSection(null);
          },
        },
      );
    },
    [addEntry, addEntryForm],
  );

  const startEditEntry = useCallback((entry: TimelineEntry) => {
    setEditingEntryId(entry.id);
    setEditEntryForm(entryToForm(entry));
    setAddingEntryToSection(null);
  }, []);

  const saveEditEntry = useCallback(() => {
    if (!editingEntryId || !editEntryForm.title.trim()) return;
    updateEntry.mutate(
      {
        entryId: editingEntryId,
        updates: {
          time: editEntryForm.time || null,
          title: editEntryForm.title.trim(),
          location: editEntryForm.location || null,
          vendorNote: editEntryForm.vendorNote || null,
          notes: editEntryForm.notes || null,
        },
      },
      { onSuccess: () => setEditingEntryId(null) },
    );
  }, [editingEntryId, editEntryForm, updateEntry]);

  const cancelEditEntry = useCallback(() => {
    setEditingEntryId(null);
    setEditEntryForm(emptyForm);
  }, []);

  const handleDeleteEntry = useCallback(
    (entryId: string) => {
      deleteEntry.mutate(entryId);
    },
    [deleteEntry],
  );

  const handleUpdateRainPlan = useCallback(
    (timelineId: string, rainPlanNotes: string) => {
      updateTimeline.mutate({ id: timelineId, updates: { rainPlanNotes } });
    },
    [updateTimeline],
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading timelines...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {isEventScoped && (
            <button
              onClick={() => navigate(`/events/${eventId}`)}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Back to event"
              data-testid="back-to-event-button"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEventScoped ? 'Event Timeline' : 'All Timelines'}
            </h1>
            {isEventScoped && event?.startAt && (
              <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                <Calendar size={14} />
                <span data-testid="event-date-header">
                  {formatEventDateRange(event.startAt, event.endAt ?? null)}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ImportTimelineButton eventId={eventId} />
          <button
            onClick={handleCreateTimeline}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">New Timeline</span>
          </button>
        </div>
      </div>

      {timelines.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Clock className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">No timelines yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {timelines.map((timeline: Timeline) => (
            <div
              key={timeline.id}
              className="bg-white rounded-lg shadow"
              data-testid={`timeline-${timeline.id}`}
            >
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleTimeline(timeline.id)}
              >
                <div className="flex items-center gap-2">
                  {expandedTimeline === timeline.id ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                  <Clock size={18} className="text-primary-600" />
                  <span className="font-medium">{timeline.title || 'Untitled Timeline'}</span>
                  <span className="text-xs text-gray-400">
                    ({timeline.sections.length} sections)
                  </span>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {!timeline.isTemplate && (
                    <SaveAsTemplateButton
                      id={timeline.id}
                      type="timeline"
                      defaultName={`${timeline.title || 'Timeline'} Template`}
                    />
                  )}
                  {eventId && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPrintTimeline(timeline);
                        }}
                        className="text-gray-400 hover:text-primary-600 p-1"
                        aria-label="Print timeline"
                        title="Print timeline"
                        data-testid={`print-timeline-${timeline.id}`}
                      >
                        <Printer size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/events/${eventId}/timeline/${timeline.id}`);
                        }}
                        className="text-gray-400 hover:text-primary-600 p-1"
                        aria-label="Open timeline editor"
                        title="Open full editor"
                        data-testid={`open-timeline-${timeline.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      exportTimeline(timeline);
                    }}
                    className="text-gray-400 hover:text-primary-600 p-1"
                    aria-label="Export timeline"
                    title="Export as JSON"
                    data-testid={`export-timeline-${timeline.id}`}
                  >
                    <Download size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTimeline(timeline.id);
                    }}
                    className="text-gray-400 hover:text-red-600 p-1"
                    aria-label="Delete timeline"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {expandedTimeline === timeline.id && (
                <div className="border-t border-gray-200 px-4 py-4">
                  {/* Rain Plan Notes */}
                  <div className="mb-4">
                    <div className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                      <CloudRain size={14} />
                      Rain Plan Notes
                    </div>
                    <textarea
                      value={timeline.rainPlanNotes || ''}
                      onChange={(e) => handleUpdateRainPlan(timeline.id, e.target.value)}
                      placeholder="Enter rain plan notes..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                      data-testid="rain-plan-notes"
                    />
                  </div>

                  {/* Sections */}
                  {timeline.sections.map((section: TSection) => (
                    <div
                      key={section.id}
                      className="mb-3 border border-gray-200 rounded-md"
                      data-testid={`section-${section.id}`}
                    >
                      <div
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer"
                        onClick={() => {
                          if (editingSectionId !== section.id) toggleSection(section.id);
                        }}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {expandedSections.has(section.id) ? (
                            <ChevronDown size={14} className="shrink-0" />
                          ) : (
                            <ChevronRight size={14} className="shrink-0" />
                          )}
                          {editingSectionId === section.id ? (
                            <div
                              className="flex items-center gap-1 flex-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="text"
                                value={sectionNameInput}
                                onChange={(e) => setSectionNameInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEditSection();
                                  if (e.key === 'Escape') cancelEditSection();
                                }}
                                autoFocus
                                className="flex-1 px-2 py-0.5 border border-gray-300 rounded text-sm font-medium"
                                data-testid={`section-name-input-${section.id}`}
                              />
                              <button
                                onClick={saveEditSection}
                                className="text-green-600 hover:text-green-700 p-0.5"
                                aria-label="Save section name"
                                data-testid={`save-section-name-${section.id}`}
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={cancelEditSection}
                                className="text-gray-400 hover:text-gray-600 p-0.5"
                                aria-label="Cancel section rename"
                                data-testid={`cancel-section-name-${section.id}`}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="text-sm font-medium truncate">{section.name}</span>
                              {section.nameHe && (
                                <span className="text-xs text-gray-400 shrink-0">
                                  {section.nameHe}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        {editingSectionId !== section.id && (
                          <div
                            className="flex items-center gap-1 shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => startEditSection(section)}
                              className="text-gray-400 hover:text-primary-600 p-0.5"
                              aria-label="Rename section"
                              data-testid={`edit-section-${section.id}`}
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              onClick={() => handleDeleteSection(section.id)}
                              className="text-gray-400 hover:text-red-600 p-0.5"
                              aria-label="Delete section"
                              data-testid={`delete-section-${section.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>

                      {expandedSections.has(section.id) && (
                        <div className="px-3 py-2">
                          {/* Entries */}
                          <div className="space-y-1">
                            {section.entries.map((entry: TimelineEntry) => (
                              <div
                                key={entry.id}
                                className="border-b border-gray-100 last:border-0"
                                data-testid={`entry-${entry.id}`}
                              >
                                {editingEntryId === entry.id ? (
                                  /* Inline edit form */
                                  <div className="py-2 space-y-2" data-testid="edit-entry-form">
                                    <div className="flex gap-2">
                                      <input
                                        type="time"
                                        value={editEntryForm.time}
                                        onChange={(e) =>
                                          setEditEntryForm((f) => ({ ...f, time: e.target.value }))
                                        }
                                        className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                                        data-testid="edit-entry-time-input"
                                      />
                                      <input
                                        type="text"
                                        value={editEntryForm.title}
                                        onChange={(e) =>
                                          setEditEntryForm((f) => ({
                                            ...f,
                                            title: e.target.value,
                                          }))
                                        }
                                        placeholder="Title"
                                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                        data-testid="edit-entry-title-input"
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        value={editEntryForm.location}
                                        onChange={(e) =>
                                          setEditEntryForm((f) => ({
                                            ...f,
                                            location: e.target.value,
                                          }))
                                        }
                                        placeholder="Location"
                                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                        data-testid="edit-entry-location-input"
                                      />
                                      <input
                                        type="text"
                                        value={editEntryForm.vendorNote}
                                        onChange={(e) =>
                                          setEditEntryForm((f) => ({
                                            ...f,
                                            vendorNote: e.target.value,
                                          }))
                                        }
                                        placeholder="Vendor note"
                                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                        data-testid="edit-entry-vendor-note-input"
                                      />
                                    </div>
                                    <input
                                      type="text"
                                      value={editEntryForm.notes}
                                      onChange={(e) =>
                                        setEditEntryForm((f) => ({ ...f, notes: e.target.value }))
                                      }
                                      placeholder="Notes"
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                      data-testid="edit-entry-notes-input"
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        onClick={saveEditEntry}
                                        disabled={!editEntryForm.title.trim()}
                                        className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50"
                                        data-testid="save-edit-entry-button"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={cancelEditEntry}
                                        className="px-3 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
                                        data-testid="cancel-edit-entry-button"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  /* Read view */
                                  <div className="flex items-start gap-3 py-1.5">
                                    <span className="text-sm font-mono text-primary-600 w-16 shrink-0">
                                      {formatTimeDisplay(entry.time) || '--:--'}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <span className="text-sm font-medium">{entry.title}</span>
                                      {entry.location && (
                                        <span className="text-xs text-gray-500 ml-2">
                                          @ {entry.location}
                                        </span>
                                      )}
                                      {entry.vendorNote && (
                                        <span className="text-xs text-gray-400 ml-2">
                                          ({entry.vendorNote})
                                        </span>
                                      )}
                                      {entry.notes && (
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          {entry.notes}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <button
                                        onClick={() => startEditEntry(entry)}
                                        className="text-gray-400 hover:text-primary-600 p-0.5"
                                        aria-label="Edit entry"
                                        data-testid={`edit-entry-${entry.id}`}
                                      >
                                        <Pencil size={12} />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteEntry(entry.id)}
                                        className="text-gray-400 hover:text-red-600 p-0.5"
                                        aria-label="Delete entry"
                                        data-testid={`delete-entry-${entry.id}`}
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Add Entry Form */}
                          {addingEntryToSection === section.id ? (
                            <div className="mt-2 space-y-2" data-testid="add-entry-form">
                              <div className="flex gap-2">
                                <input
                                  type="time"
                                  value={addEntryForm.time}
                                  onChange={(e) =>
                                    setAddEntryForm((f) => ({ ...f, time: e.target.value }))
                                  }
                                  className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                                  data-testid="entry-time-input"
                                />
                                <input
                                  type="text"
                                  value={addEntryForm.title}
                                  onChange={(e) =>
                                    setAddEntryForm((f) => ({ ...f, title: e.target.value }))
                                  }
                                  placeholder="Title *"
                                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                  data-testid="entry-title-input"
                                />
                              </div>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={addEntryForm.location}
                                  onChange={(e) =>
                                    setAddEntryForm((f) => ({ ...f, location: e.target.value }))
                                  }
                                  placeholder="Location"
                                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                  data-testid="entry-location-input"
                                />
                                <input
                                  type="text"
                                  value={addEntryForm.vendorNote}
                                  onChange={(e) =>
                                    setAddEntryForm((f) => ({
                                      ...f,
                                      vendorNote: e.target.value,
                                    }))
                                  }
                                  placeholder="Vendor note"
                                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                  data-testid="entry-vendor-note-input"
                                />
                              </div>
                              <input
                                type="text"
                                value={addEntryForm.notes}
                                onChange={(e) =>
                                  setAddEntryForm((f) => ({ ...f, notes: e.target.value }))
                                }
                                placeholder="Notes"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                data-testid="entry-notes-input"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleAddEntry(section.id)}
                                  disabled={!addEntryForm.title.trim()}
                                  className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50"
                                  data-testid="entry-save-button"
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => {
                                    setAddingEntryToSection(null);
                                    setAddEntryForm(emptyForm);
                                  }}
                                  className="px-3 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
                                  data-testid="entry-cancel-button"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setAddingEntryToSection(section.id);
                                setEditingEntryId(null);
                              }}
                              className="mt-2 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                              data-testid={`add-entry-button-${section.id}`}
                            >
                              <Plus size={12} /> Add Entry
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add Section */}
                  <div className="flex gap-2 mt-3">
                    <input
                      type="text"
                      value={newSectionName}
                      onChange={(e) => setNewSectionName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddSection(timeline.id);
                      }}
                      placeholder="New section name"
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm"
                      data-testid="new-section-input"
                    />
                    <button
                      onClick={() => handleAddSection(timeline.id)}
                      disabled={!newSectionName.trim()}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 disabled:opacity-50"
                      data-testid="add-section-button"
                    >
                      Add Section
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {printTimeline && (
        <TimelinePrintView
          timeline={printTimeline}
          eventName={event?.name ?? 'Event'}
          eventTags={eventTags}
          appLogoUrl={appLogoUrl ?? null}
          eventLogoUrl={event?.logoUrl ?? null}
          onClose={() => setPrintTimeline(null)}
        />
      )}
    </div>
  );
}
